/*
  FD brand: the fabric drawer.

  A collapsed summary row ("Teddy · 8 fabrics · 57 colours available") that opens
  a slide-over panel of showcase cards, one per fabric. Picking a card commits it
  and closes the panel -- one tap, no separate Confirm step (user ruling
  2026-08-27). Committing does exactly ONE thing: it clicks the first usable
  radio in that fabric's group inside the colour fieldset. Everything after that
  -- variant id, price, gallery, the resolver's "does not apply" pass -- happens
  on Dawn's own change path, identical to a shopper clicking a swatch directly.
  Nothing about pricing, media or variants is reimplemented here.

  Nothing about fabrics is written into this file either. The cards are built
  from the colour fieldset's `.fd-swgroup` markup: the group name, its swatch
  count and its price-difference badge all come from Liquid, so the panel can
  only ever repeat what the option values and variant prices already say.

  This element sits outside <variant-selects>, whose contents Dawn replaces
  wholesale on every option change -- so it survives, and repaints itself from
  the re-rendered radios via a MutationObserver. The panel's open state
  therefore survives a re-render too.

  The shell (scrim, focus trap, Escape, scroll lock) is deliberately generic and
  exported on window.FdChooser: the seat-comfort row is the same anatomy and
  should reuse it rather than grow a second copy.
*/
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  /*
    One panel shell: row button <-> scrim + slide-over. hooks.onOpen runs before
    the panel is shown (that is where a draft is reset). Callers commit and
    close() themselves when a card is picked; Escape, the scrim and the x all
    discard.

    The closed panel is left `inert`. Panels whose markup already ships inert
    (the fabric drawer) are therefore inert from first paint; the others become
    inert after their first close, which is all this shell can promise without
    knowing whether a caller's no-JS fallback renders the panel inline.
  */
  function shell(parts, hooks) {
    var row = parts.row;
    var panel = parts.panel;
    var scrim = parts.scrim;
    var lastFocus = null;
    var open = false;

    function focusFirst() {
      var items = focusables(panel);
      if (items.length) items[0].focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables(panel);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function show() {
      if (open) return;
      open = true;
      lastFocus = document.activeElement;
      if (hooks.onOpen) hooks.onOpen();
      scrim.classList.add('is-open');
      panel.classList.add('is-open');
      // `inert` rather than `hidden`: hidden would kill the slide transition.
      // Cleared before focus moves, or focusFirst() has nothing to focus.
      panel.removeAttribute('inert');
      row.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('fd-oc-locked');
      document.addEventListener('keydown', onKeydown);
      // The panel's visibility flips on this same tick (it is stepped, not
      // interpolated, in CSS) so focus lands immediately; the extra frame is a
      // backstop for engines that defer the style recalc.
      focusFirst();
      requestAnimationFrame(function () {
        if (!panel.contains(document.activeElement)) focusFirst();
      });
    }

    function close() {
      if (!open) return;
      open = false;
      document.removeEventListener('keydown', onKeydown);
      scrim.classList.remove('is-open');
      panel.classList.remove('is-open');
      row.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('fd-oc-locked');
      // Focus first, then inert: setting it while focus is still inside the
      // panel would drop focus to <body> instead of returning it to the row.
      (lastFocus && document.contains(lastFocus) ? lastFocus : row).focus();
      panel.setAttribute('inert', '');
    }

    row.addEventListener('click', show);
    scrim.addEventListener('click', close);
    var closeBtn = panel.querySelector('[data-fd-oc-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);

    return {
      show: show,
      close: close,
      isOpen: function () {
        return open;
      }
    };
  }

  window.FdChooser = window.FdChooser || {};
  window.FdChooser.shell = shell;
  window.FdChooser.focusables = focusables;

  if (customElements.get('fd-fabric-drawer')) return;

  customElements.define(
    'fd-fabric-drawer',
    class FdFabricDrawer extends HTMLElement {
      connectedCallback() {
        this.row = this.querySelector('[data-fd-oc-row]');
        this.panel = this.querySelector('.fd-ocpanel');
        this.scrim = this.querySelector('[data-fd-oc-scrim]');
        this.nameEl = this.querySelector('[data-fd-fabric-name]');
        this.thumbEl = this.querySelector('[data-fd-fabric-thumb]');
        this.cardsEl = this.querySelector('[data-fd-fabric-cards]');
        if (!this.row || !this.panel || !this.scrim || !this.cardsEl) return;

        this.draft = null;
        this.signature = '';

        this.chooser = shell(
          { row: this.row, panel: this.panel, scrim: this.scrim },
          {
            onOpen: () => {
              this.draft = this.currentGroupName();
              this.paintDraft();
            }
          }
        );

        // Dawn replaces <variant-selects>'s contents on every option change, so
        // the fieldset this row describes is a different element each time.
        // Watch the resolver (the nearest thing that survives) instead of
        // holding a reference.
        this.scope = this.closest('fd-option-resolver') || this.parentElement || document;
        this.observer = new MutationObserver(() => this.schedule());
        this.observer.observe(this.scope, { childList: true, subtree: true });
        this.schedule();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        clearTimeout(this.queuedTimer);
        this.queued = false;
        clearTimeout(this.paintRetry);
      }

      schedule() {
        if (this.queued) return;
        this.queued = true;
        // A fresh mutation is fresh evidence, so it also clears the retry
        // ceiling below -- otherwise a picker that stayed ambiguous for three
        // seconds once would never be painted again.
        this.paintTries = 0;
        this.queuedTimer = window.setTimeout(() => {
          this.queued = false;
          this.paint();
        }, 0);
      }

      /*
        Dawn's view transition briefly leaves the outgoing and incoming pickers
        in the DOM together (fd-option-resolve.js hits the same window). Painting
        then would read the copy that is about to be thrown away.
      */
      fieldset() {
        var found = this.scope.querySelectorAll('[data-fd-colour-option]');
        if (found.length !== 1) return null;
        return found[0];
      }

      currentGroup() {
        var fieldset = this.fieldset();
        if (!fieldset) return null;
        var checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.closest('.fd-swgroup') : fieldset.querySelector('.fd-swgroup--sel');
      }

      currentGroupName() {
        var group = this.currentGroup();
        return group ? group.dataset.fdGroup : null;
      }

      paint() {
        var fieldset = this.fieldset();
        if (!fieldset) {
          // Bounded: a detached element, or a picker that never resolves to a
          // single fieldset, must not leave a timer spinning for the life of
          // the page. ~3s of retries is far longer than Dawn's view
          // transition; past that the MutationObserver is the way back in --
          // schedule() resets paintTries, so the ceiling is never permanent.
          if (!this.isConnected) return;
          this.paintTries = (this.paintTries || 0) + 1;
          if (this.paintTries > 50) return;
          clearTimeout(this.paintRetry);
          this.paintRetry = window.setTimeout(() => this.paint(), 60);
          return;
        }
        this.paintTries = 0;
        clearTimeout(this.paintRetry);

        var group = this.currentGroup();
        if (!group) return;

        if (this.nameEl) this.nameEl.textContent = group.dataset.fdGroup;
        this.row.setAttribute(
          'aria-label',
          (this.dataset.rowLabel || 'Fabric') + ': ' + group.dataset.fdGroup
        );

        // The thumb is the selected swatch itself, cloned -- so a confirmed
        // colour shows its real fill and an unphotographed one keeps its honest
        // dashed placeholder. No second swatch lookup, no chance of divergence.
        var checked = fieldset.querySelector('input[type="radio"]:checked');

        /*
          Dawn optimistically writes the raw option value into the legend on
          every radio change (global.js updateSelectionMetadata:
          `selectedSwatchValue.innerHTML = value`), which puts "Jumbo Cord —
          Coffee" back into a legend that is only allowed to say "Coffee" --
          the fabric is named by the row above. Rather than fork a vanilla Dawn
          file, the value is put right here, on the same tick, and the server
          render agrees when it lands. Written only when it differs, or the
          write would retrigger this observer forever.
        */
        var legendValue = fieldset.querySelector('[data-selected-value]');
        var separator = this.dataset.separator;
        if (legendValue && checked && separator) {
          var parts = checked.value.split(separator);
          var colour = parts[parts.length - 1].trim();
          if (legendValue.textContent.trim() !== colour) legendValue.textContent = colour;
        }

        var box = checked ? checked.parentElement.querySelector('.fd-sw__box') : null;
        if (this.thumbEl && box) {
          var fill = box.classList.contains('fd-sw__box--fill');
          var img = box.classList.contains('fd-sw__box--img');
          this.thumbEl.classList.toggle('fd-orow__thumb--fill', fill);
          this.thumbEl.classList.toggle('fd-orow__thumb--img', img);
          this.thumbEl.setAttribute('style', box.getAttribute('style') || '');
          this.thumbEl.textContent = fill ? '' : box.textContent.trim();
        }

        this.buildCards(fieldset);
        if (this.chooser.isOpen()) this.paintDraft();
      }

      buildCards(fieldset) {
        var groups = Array.prototype.slice.call(fieldset.querySelectorAll('.fd-swgroup'));
        var signature = groups
          .map(function (g) {
            return (
              g.dataset.fdGroup +
              ':' +
              g.querySelectorAll('.fd-opt--sw').length +
              ':' +
              (g.querySelector('.fd-swgroup__uplift')?.textContent.trim() || '') +
              ':' +
              (g.querySelector('.fd-sw__box--img') ? 'p' : '') +
              ':' +
              // The selected group's card follows the checked colour, so which
              // colour is checked is part of what the cards were built from.
              (g.querySelector('input[type="radio"]:checked')?.value || '')
            );
          })
          .join('|');
        if (signature === this.signature) return;
        this.signature = signature;

        var countTemplate = this.dataset.cardCountTemplate || '{count} colours';
        var imageNote = this.dataset.cardImageNote || '';
        this.cardsEl.textContent = '';

        groups.forEach((groupEl) => {
          var name = groupEl.dataset.fdGroup;
          var count = groupEl.querySelectorAll('.fd-opt--sw').length;
          var uplift = groupEl.querySelector('.fd-swgroup__uplift');

          var card = document.createElement('button');
          card.type = 'button';
          card.className = 'fd-occard';
          card.dataset.fdGroup = name;
          card.setAttribute('aria-pressed', 'false');

          /*
            The card's picture never claims a colour the shopper did not pick
            (user ruling 2026-08-17): in the fabric that holds the selection it
            is the selected colour's own photo or nothing -- an unphotographed
            selection gets the honest text placeholder, not a sibling colour's
            picture. A fabric with no selection has made no claim yet, so its
            card may showcase its first photographed colour. Cloning the tile's
            style attribute reuses the exact same CDN URLs the tiles carry.
          */
          var checkedInput = groupEl.querySelector('input[type="radio"]:checked');
          var photoBox = checkedInput
            ? checkedInput.parentElement.querySelector('.fd-sw__box--img')
            : groupEl.querySelector('.fd-sw__box--img');

          var im = document.createElement('span');
          im.className = 'fd-occard__im';
          im.setAttribute('aria-hidden', 'true');
          if (photoBox) {
            im.classList.add('fd-occard__im--photo');
            im.setAttribute('style', photoBox.getAttribute('style') || '');
          } else if (imageNote) {
            im.textContent = imageNote;
          }
          if (photoBox || imageNote) card.appendChild(im);

          var cap = document.createElement('span');
          cap.className = 'fd-occard__cap';
          cap.textContent = name;
          if (uplift) cap.appendChild(uplift.cloneNode(true));
          card.appendChild(cap);

          var sub = document.createElement('span');
          sub.className = 'fd-occard__sub';
          sub.textContent = countTemplate.replace('{count}', String(count));
          card.appendChild(sub);

          // One tap does it all: mark the card, click the radio, slide away.
          // A tap on the fabric already selected commits nothing and just
          // closes -- same outcome the old Confirm gave it.
          card.addEventListener('click', () => {
            this.draft = name;
            this.paintDraft();
            this.commit();
            this.chooser.close();
          });

          this.cardsEl.appendChild(card);
        });

        this.paintDraft();
      }

      paintDraft() {
        var draft = this.draft || this.currentGroupName();
        Array.prototype.forEach.call(this.cardsEl.children, function (card) {
          card.setAttribute('aria-pressed', String(card.dataset.fdGroup === draft));
        });
      }

      /*
        The whole commit: click a radio. A fabric's colours are its own, so the
        landing colour is that fabric's first orderable one -- .disabled is how
        Dawn marks a combination that does not exist, and a fabric with none is
        still clickable so the shopper gets Dawn's own unavailable state rather
        than a silent no-op.
      */
      commit() {
        if (!this.draft || this.draft === this.currentGroupName()) return;
        var fieldset = this.fieldset();
        if (!fieldset) return;
        var target = Array.prototype.find.call(
          fieldset.querySelectorAll('.fd-swgroup'),
          (g) => g.dataset.fdGroup === this.draft
        );
        if (!target) return;
        var input =
          target.querySelector('input[type="radio"]:not(.disabled)') ||
          target.querySelector('input[type="radio"]');
        if (input) input.click();
      }
    }
  );
})();
